<template>
    <div>
        <div class="Buttons">
            <QBtn
                color="primary"
                dense
                noCaps
                outline
                :label="$t('visualization.contextComparison.addContext')"
                icon="add_circle_outline"
                :disable="localSections.length >= 8 || isLoading"
                @click="addSection"
            />
            <QBtn
                color="primary"
                dense
                noCaps
                outline
                :label="$t('visualization.contextComparison.saveContext')"
                icon="save"
                :disable="!pendingChanges || isLoading"
                @click="saveContext"
            />
            <QBtn
                color="primary"
                dense
                noCaps
                outline
                :label="$t('visualization.contextComparison.downloadCSV')"
                icon="download"
                @click="downloadCSV"
            />
            <div v-if="pendingChanges" class="WarningText">
                {{ $t('visualization.contextComparison.pendingChanges') }}
            </div>
        </div>
        <div v-if="!isLoading" class="cardsContainer">
            <div
                v-if="localSections.length === 0"
                class="noDataContainer"
            >
                <div class="noData text-h6 text-weight-medium">
                    {{ $t('visualization.contextComparison.noData') }}
                </div>
                <div class="noDataExplanation text-body2 text-grey-7">
                    {{ $t('visualization.contextComparison.noDataExplanation') }}
                </div>
            </div>
            <q-card
                v-for="(section, index) in localSections"
                :key="section.sectionId"
                flat
                bordered
                class="contextComparisonContainer q-pa-md relative-position"
            >
                <div class="q-px-md row justify-between items-center">
                    <span class="text-weight-bold">
                        {{ `${$t('visualization.contextComparison.comparison')} ${index + 1}` }}
                    </span>
                    <q-btn
                        flat
                        color="primary"
                        icon="close"
                        class="absolute-top-right"
                        @click="removeSection(section)"
                    />
                </div>
                <ContextComparisonCard
                    :filteredInfo="filteredInfo"
                    :sectionAssociated="section"
                    :processId="processId"
                    :datasets="datasets"
                    @filter-selected="saveFilterSelected"
                    @csv-position="CSVList"
                />
            </q-card>
        </div>
        <q-inner-loading :showing="isLoading" />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { filtersStorageMixin } from '@/mixins'
import { download } from '@/services/download'
import Notify from '@/services/notify'
import {
    Api, apiRequest, notifyError,
    notifySuccess,
} from '@/api'
import ContextComparisonCard from './ContextComparisonCard.vue'

export default {
    name: 'ContextComparison',
    components: {
        ContextComparisonCard,
    },
    mixins: [filtersStorageMixin],
    props: {
        filteredInfo: VueTypes.oneOfType([VueTypes.object, VueTypes.array]),
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
    },
    data() {
        return {
            filtersSaved: null,
            csvList: [],
            localSections: [],
            pendingChanges: false,
            initialData: [],
            datasets: null,
            isLoadingDatasets: false,
            isLoadingComparisons: false,
        }
    },
    computed: {
        isLoading() {
            return this.isLoadingDatasets || this.isLoadingComparisons
        },
    },
    mounted() {
        this.getDatasets()
        this.getComparisons()
    },
    methods: {
        getComparisons() {
            this.isLoadingComparisons = true
            apiRequest(Api().visualizations.getComparisons({ datasetUid: this.processId }))
                .then((res) => {
                    const mapped = res.map((entry, index) => ({
                        comparedDatasetId: entry.comparedDatasetId,
                        datasetId: entry.datasetId,
                        filtersetId: entry.filtersetId,
                        sectionId: index,
                    }))
                    this.localSections = JSON.parse(JSON.stringify(mapped))
                    this.initialData = JSON.parse(JSON.stringify(mapped))
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoadingComparisons = false
                })
        },
        getDatasets() {
            this.isLoadingDatasets = true
            const params = {
                projectUid: this.$route.params.projectId,
            }
            apiRequest(Api().datasets.list(params))
                .then(({ data }) => {
                    this.datasets = data.filter((item) => item.state.identifier !== 'ERROR')
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoadingDatasets = false
                })
        },
        addSection() {
            if (this.localSections.length <= 7) {
                const nextId = this.checkIdAvailable()
                const newSection = {
                    sectionId: nextId,
                    datasetId: this.processId,
                    comparedDatasetId: null,
                    filtersetId: null,
                }
                this.localSections.push(newSection)
                this.checkIfChangesSaved()
            } else {
                const notify = ({ message, type = 'negative' }) => Notify.create({
                    message,
                    color: type,
                })
                const mes = this.$t('visualization.contextComparison.maxContext')
                notify({ message: mes })
            }
        },
        downloadCSV() {
            const csvContent = this.csvList.reduce((acc, obj) => {
                if (acc === `${Object.keys(this.csvList[0].data).join(',')}\n`) {
                    const headers = Object.keys(this.csvList[0].data).map((header) => {
                        if (header === 'endDateDifference' || header === 'startDateDifference') {
                            return `${header} ${this.$t('visualization.contextComparison.seconds')}`
                        }
                        return header
                    }).join(',')
                    // eslint-disable-next-line no-param-reassign
                    acc = `${headers}\n`
                }
                const row = Object.values(obj.data).join(',')
                return `${acc}${row}\n`
            }, `${Object.keys(this.csvList[0].data).join(',') }\n`)
            download({ name: this.$t('visualization.tabs.contextComparison'), data: csvContent })
            notifySuccess(this.$t('visualization.traces.downloadOK'), (this.isLoading = false))
        },
        CSVList(data) {
            const existingIndex = this.csvList.findIndex((item) => item.id === data.id)
            if (existingIndex !== -1) {
                this.csvList[existingIndex] = data
            } else {
                this.csvList.push(data)
            }
        },
        async removeSection(section) {
            const index = this.localSections.indexOf(section)
            this.localSections.splice(index, 1)
            this.csvList.splice(index, 1)
            this.checkIfChangesSaved()
        },
        saveFilterSelected(dataset) {
            const section = this.localSections.find((s) => s.sectionId === dataset.sectionId)
            if (section) {
                section.comparedDatasetId = dataset.comparedDatasetId
                section.filtersetId = dataset.filtersetId
            }
            this.checkIfChangesSaved()
        },
        checkIfChangesSaved() {
            const serialize = ({ comparedDatasetId, filtersetId, sectionId }) => JSON.stringify({ comparedDatasetId, filtersetId, sectionId })
            if (this.localSections.length !== this.initialData.length) {
                this.pendingChanges = true
                return
            }
            const set1 = new Set(this.localSections.map(serialize))
            const set2 = new Set(this.initialData.map(serialize))
            this.pendingChanges = ![...set1].every((item) => set2.has(item))
        },
        checkIdAvailable() {
            const biggestId = this.localSections.reduce((s, obj) => Math.max(s, obj.sectionId), 0)
            return biggestId + 1
        },
        saveContext() {
            const params = {
                comparisons: this.localSections.map(({ comparedDatasetId, filtersetId }) => ({
                    datasetId: this.processId,
                    comparedDatasetId,
                    filtersetId,
                })),
            }
            apiRequest(Api().visualizations.saveComparisons({ datasetUid: this.processId, params }))
                .then(() => {
                    notifySuccess(this.$t('visualization.contextComparison.savedContext'))
                    this.initialData = JSON.parse(JSON.stringify(this.localSections))
                    this.checkIfChangesSaved()
                })
                .catch(notifyError)
                .finally(() => {
                })
        },
    },
}
</script>

<style scoped lang="scss">
.closeContainer {
    display: inline-block;
}
.contextComparisonContainer{
    margin: 20px;
    width: 22%;
    min-width: 368px;
    display: inline-block;
}
.closeContainer{
    display: flex;
}
.WarningText {
    color: $warning;
    font-weight: bold;
}
.Buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}

.cardsContainer {
    min-height: 60vh;
}

.noDataContainer {
    height: 100%;
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 8px;
    padding: 16px;
}

.noDataExplanation {
    max-width: 640px;
}
</style>
